/**
 * Wealth services — investments, debts, goals.
 *
 * @module services/wealthService
 */
import { api } from "../lib/api";

/**
 * @typedef {Object} ListParams
 * @property {number} [limit]
 * @property {number} [skip]
 */

export const investmentsService = {
  /**
   * @param {ListParams} [params]
   * @returns {Promise<import("./types").Investment[]>}
   */
  list: (params = {}) =>
    api.get("/investments", { params }).then((r) => r.data),

  /**
   * @param {Omit<import("./types").Investment,"id">} payload
   * @returns {Promise<import("./types").Investment>}
   */
  create: (payload) => api.post("/investments", payload).then((r) => r.data),

  /**
   * @param {string} id
   * @param {Omit<import("./types").Investment,"id">} payload
   * @returns {Promise<import("./types").Investment>}
   */
  update: (id, payload) => api.put(`/investments/${id}`, payload).then((r) => r.data),

  /** @param {string} id @returns {Promise<{ok:true}>} */
  remove: (id) => api.delete(`/investments/${id}`).then((r) => r.data),
};

export const debtsService = {
  /**
   * @param {ListParams} [params]
   * @returns {Promise<import("./types").Debt[]>}
   */
  list: (params = {}) =>
    api.get("/debts", { params }).then((r) => r.data),

  /**
   * @param {Omit<import("./types").Debt,"id">} payload
   * @returns {Promise<import("./types").Debt>}
   */
  create: (payload) => api.post("/debts", payload).then((r) => r.data),

  /**
   * @param {string} id
   * @param {Omit<import("./types").Debt,"id">} payload
   * @returns {Promise<import("./types").Debt>}
   */
  update: (id, payload) => api.put(`/debts/${id}`, payload).then((r) => r.data),

  /** @param {string} id @returns {Promise<{ok:true}>} */
  remove: (id) => api.delete(`/debts/${id}`).then((r) => r.data),

  /**
   * @param {string} id
   * @param {number} amount
   * @returns {Promise<import("./types").Debt>}
   */
  pay: (id, amount) => api.post(`/debts/${id}/pay`, { amount }).then((r) => r.data),
};

export const goalsService = {
  /**
   * @param {ListParams} [params]
   * @returns {Promise<import("./types").Goal[]>}
   */
  list: (params = {}) =>
    api.get("/goals", { params }).then((r) => r.data),

  /**
   * @param {Omit<import("./types").Goal,"id">} payload
   * @returns {Promise<import("./types").Goal>}
   */
  create: (payload) => api.post("/goals", payload).then((r) => r.data),

  /**
   * @param {string} id
   * @param {Omit<import("./types").Goal,"id">} payload
   * @returns {Promise<import("./types").Goal>}
   */
  update: (id, payload) => api.put(`/goals/${id}`, payload).then((r) => r.data),

  /** @param {string} id @returns {Promise<{ok:true}>} */
  remove: (id) => api.delete(`/goals/${id}`).then((r) => r.data),

  /**
   * @param {string} id
   * @param {number} amount
   * @returns {Promise<import("./types").Goal>}
   */
  contribute: (id, amount) =>
    api.post(`/goals/${id}/contribute`, { amount }).then((r) => r.data),
};
